import { message } from 'antd'
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form'

import getErrorMessage from '~/lib/getErrorMessage'
import logger from '~/lib/logger'
import {
    VERIFICATION_CODE,
    PASSWORD,
    DEFAULT_VERIFY_2FA_ERROR_MESSAGE,
    DEFAULT_DISABLE_2FA_ERROR_MESSAGE,
    PERMISSION_DENIED,
    INCORRECT_PASSWORD,
} from '../constants'

export const handleVerify2FA = ({
    id,
    form,
    closeModal,
    setLoading,
    verify2FA,
    updateMfaStatus,
}: {
    id: number
    form: WrappedFormUtils
    closeModal: () => void
    setLoading: (loading: boolean) => void
    verify2FA: Function
    updateMfaStatus: (mafStatus: boolean) => void
}) => {
    form.validateFields(async (error, values) => {
        if (error) {
            return
        }
        setLoading(true)
        try {
            const { data } = await verify2FA({
                variables: {
                    id,
                    code: values[VERIFICATION_CODE],
                },
            })
            const mfaStatus = data?.user?.verify2FA?.is2faEnabled

            if (mfaStatus) {
                setLoading(false)
                updateMfaStatus(mfaStatus)
                closeModal()
            } else {
                throw new Error(DEFAULT_VERIFY_2FA_ERROR_MESSAGE)
            }
        } catch (err) {
            message.error(
                getErrorMessage(err) || DEFAULT_VERIFY_2FA_ERROR_MESSAGE,
            )
            logger.exception(err, {
                extra: {
                    info: 'MyAccountPage:: Failed to enable 2FA',
                    id,
                },
            })
            setLoading(false)
        }
    })
}

export const handleDisable2FA = ({
    id,
    form,
    closeModal,
    setLoading,
    disable2FA,
    updateMfaStatus,
}: {
    id: number
    form: WrappedFormUtils
    closeModal: () => void
    setLoading: (loading: boolean) => void
    disable2FA: Function
    updateMfaStatus: (mafStatus: boolean) => void
}) => {
    form.validateFields(async (error, values) => {
        if (error) {
            return
        }
        setLoading(true)
        try {
            const { data } = await disable2FA({
                variables: {
                    id,
                    password: values[PASSWORD],
                },
            })
            const mfaStatus = data?.user?.disable2FA?.is2faEnabled

            if (data && !mfaStatus) {
                setLoading(false)
                updateMfaStatus(mfaStatus)
                closeModal()
            } else {
                throw new Error(DEFAULT_DISABLE_2FA_ERROR_MESSAGE)
            }
        } catch (err) {
            const returnedError = getErrorMessage(err)
            const errorMessage =
                returnedError === PERMISSION_DENIED
                    ? INCORRECT_PASSWORD
                    : returnedError

            message.error(errorMessage || DEFAULT_DISABLE_2FA_ERROR_MESSAGE)
            logger.exception(err, {
                extra: {
                    info: 'MyAccountPage:: Failed to disable 2FA',
                    id,
                },
            })
            setLoading(false)
        }
    })
}

export const generatePasswordLengthRule = (min: number, max: number) => {
    const maxRule = {
        max,
        message: `Password cannot exceed max ${max} characters.`,
    }

    const minRule = {
        min,
        message: `Password must be minimum ${min} characters.`,
    }

    if (min) {
        return [minRule, maxRule]
    }

    return [maxRule]
}
